import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { SITENAME, LOGO, ASSETS } from "../utils/init";
import { showToast, showHttpError } from "../utils/library";
import {
  isCryptoAddrValid,
  updateRefundAddress,
  getRefundDetails,
} from "../utils/service";
import Loader from "../components/Loader";

const RefundAddress = () => {
  const { token } = useParams();

  const [walletAddress, setWalletAddress] = useState("");
  const [memo, setMemo] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [networks, setNetworks] = useState([]);
  const [addressError, setAddressError] = useState("");
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [validatingAddress, setValidatingAddress] = useState(false);

  // These would come from URL params or API response
  const [refundData, setRefundData] = useState({
    merchantName: "",
    refundAmount: "",
    currency: "",
    networkName: "",
    orderId: "",
    transactionId: "",
    invoiceAmount: "",
    networkFeeAmount: "",
  });

  useEffect(() => {
    if (token) {
      fetchRefundDetails();
    }
  }, [token]);
  console.log("Refund Data:", refundData);

  useEffect(() => {
    if (refundData.currency) {
      const asset = ASSETS.find((a) => a.assetCode === refundData.currency);
      if (asset) {
        setNetworks(asset.network);
        if (asset.network.length === 1) {
          setSelectedNetwork(asset.network[0]);
        }
      }
    }
  }, [refundData.currency]);

  const fetchRefundDetails = () => {
    setShowLoader(true);
    getRefundDetails(token)
      .then((response) => {
        setShowLoader(false);
        if (response.data && response.data.status) {
          const refund = response.data.data?.refund || {};
          setRefundData({
            merchantName: refund.merchantName || "",
            refundAmount: refund.refundAmount ?? "",
            currency: refund.currency || "",
            networkName: refund.network || "",
            orderId: refund.id || "",
            transactionId: refund.transactionId || "",
            invoiceAmount: refund.invoiceAmount ?? "",
            networkFeeAmount: refund.networkFeeAmount ?? "",
          });
        } else {
          showToast(
            "ERROR",
            response.data?.message || "Unable to fetch refund details."
          );
        }
      })
      .catch((error) => {
        setShowLoader(false);
        showHttpError(error);
      });
  };

  const validateAddress = () => {
    if (!walletAddress.trim()) {
      setAddressError("Please enter a wallet address.");
      return;
    }

    setValidatingAddress(true);
    setAddressError("");
    setIsAddressValid(false);

    const networkToValidate = selectedNetwork || refundData.networkName;
    const asset = ASSETS.find((a) => a.assetCode === refundData.currency);
    const obj = {
      address: walletAddress.trim(),
      currency: refundData.currency,
      currencyId: asset ? asset.assetId : "",
      tokenType: networkToValidate,
    };

    isCryptoAddrValid(obj)
      .then((response) => {
        setValidatingAddress(false);
        console.log("Address Validation Response:", response.data);
        const res = response.data[0];
        if (res?.error === "0") {
          setIsAddressValid(true);
          setAddressError(res.error_msg);
          handleSubmit();
        } else {
          setIsAddressValid(false);
          setAddressError(res.error_msg);
        }
      })
      .catch((error) => {
        setValidatingAddress(false);
        setIsAddressValid(false);
        setAddressError(
          "Something went wrong while validating the address. Please try again."
        );
      });
  };

  const supportsMemo = refundData.currency === "XRP";

  const handleSubmit = () => {
    setShowLoader(true);
    const obj = {
      refundToken: token,
      refundAddress: walletAddress.trim(),
      memo: supportsMemo ? memo.trim() : "",
    };

    updateRefundAddress(obj)
      .then((response) => {
        setShowLoader(false);
        if (response.data && response.data.status) {
          setIsSubmitted(true);
        } else {
          showToast(
            "ERROR",
            response.data?.error_msg || "Something went wrong."
          );
        }
      })
      .catch((error) => {
        setShowLoader(false);
        showHttpError(error);
      });
  };

  const getCurrencyIcon = () => {
    const asset = ASSETS.find((a) => a.assetCode === refundData.currency);
    return asset ? asset.assetImage : null;
  };

  // ─── Styles ─────────────────────────────────────────────────
  const styles = {
    pageWrapper: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "var(--body-color)",
      padding: "20px",
    },
    card: {
      width: "100%",
      maxWidth: "520px",
      background: "var(--popupbody-color)",
      borderRadius: "16px",
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
      overflow: "hidden",
    },
    cardHeader: {
      background: "var(--navbar-color)",
      padding: "28px 32px",
      textAlign: "center",
    },
    headerIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      background: "rgba(255,255,255,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 14px",
    },
    headerTitle: {
      color: "#ffffff",
      fontSize: "22px",
      fontWeight: "600",
      margin: "0 0 6px",
    },
    merchantName: {
      color: "rgba(255,255,255,0.8)",
      fontSize: "14px",
      margin: 0,
    },
    cardBody: {
      padding: "32px",
    },
    refundBadge: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      background: "var(--body-color-less)",
      borderRadius: "10px",
      padding: "14px 20px",
      marginBottom: "28px",
    },
    refundLabel: {
      fontSize: "13px",
      color: "var(--text-color)",
      opacity: 0.6,
      margin: 0,
    },
    refundValue: {
      fontSize: "22px",
      fontWeight: "700",
      color: "var(--text-color)",
      margin: 0,
    },
    currencyTag: {
      fontSize: "14px",
      fontWeight: "500",
      color: "var(--text-color)",
      opacity: 0.7,
    },
    currencyIcon: {
      width: "28px",
      height: "28px",
      marginRight: "4px",
    },
    description: {
      fontSize: "14px",
      color: "var(--text-color)",
      opacity: 0.7,
      lineHeight: "1.6",
      marginBottom: "24px",
      textAlign: "center",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: "600",
      color: "var(--text-color)",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      fontSize: "14px",
      border: "1.5px solid var(--body-color-less2)",
      borderRadius: "10px",
      background: "var(--body-color)",
      color: "var(--text-color)",
      outline: "none",
      transition: "border-color 0.2s ease",
      boxSizing: "border-box",
    },
    inputFocus: {
      borderColor: "var(--btn-color)",
    },
    inputError: {
      borderColor: "#e74c3c",
    },
    errorText: {
      display: "flex",
      alignItems: "flex-start",
      gap: "6px",
      color: "#e74c3c",
      fontSize: "12.5px",
      marginTop: "8px",
      lineHeight: "1.4",
    },
    successText: {
      display: "flex",
      alignItems: "flex-start",
      gap: "6px",
      color: "#27ae60",
      fontSize: "12.5px",
      marginTop: "8px",
      lineHeight: "1.4",
    },
    inputSuccess: {
      borderColor: "#27ae60",
    },
    networkSection: {
      marginTop: "20px",
      marginBottom: "24px",
    },
    networkButtons: {
      display: "flex",
      gap: "10px",
      flexWrap: "wrap",
    },
    networkBtn: {
      padding: "8px 18px",
      fontSize: "13px",
      fontWeight: "500",
      borderRadius: "8px",
      border: "1.5px solid var(--body-color-less2)",
      background: "var(--body-color)",
      color: "var(--text-color)",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    networkBtnActive: {
      padding: "8px 18px",
      fontSize: "13px",
      fontWeight: "600",
      borderRadius: "8px",
      border: "1.5px solid var(--btn-color)",
      background: "var(--btn-color)",
      color: "#ffffff",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    submitBtn: {
      width: "100%",
      padding: "14px",
      fontSize: "15px",
      fontWeight: "600",
      color: "#ffffff",
      background: "var(--btn-color)",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "opacity 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    submitBtnDisabled: {
      opacity: 0.6,
      cursor: "not-allowed",
    },
    // Success page styles
    successWrapper: {
      textAlign: "center",
      padding: "8px 0",
    },
    successIcon: {
      width: "72px",
      height: "72px",
      borderRadius: "50%",
      background: "rgba(39, 174, 96, 0.1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 20px",
    },
    successTitle: {
      fontSize: "20px",
      fontWeight: "700",
      color: "var(--text-color)",
      marginBottom: "8px",
    },
    successSubtitle: {
      fontSize: "14px",
      color: "var(--text-color)",
      opacity: 0.65,
      marginBottom: "28px",
      lineHeight: "1.5",
    },
    successCard: {
      background: "var(--body-color-less)",
      borderRadius: "10px",
      padding: "20px",
      marginBottom: "24px",
    },
    successRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
    },
    successLabel: {
      fontSize: "13px",
      color: "var(--text-color)",
      opacity: 0.6,
    },
    successValue: {
      fontSize: "14px",
      fontWeight: "600",
      color: "var(--text-color)",
    },
    divider: {
      height: "1px",
      background: "var(--body-color-less2)",
      margin: "4px 0",
    },
    infoNote: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      background: "rgba(40, 76, 207, 0.06)",
      borderRadius: "10px",
      padding: "14px 16px",
      textAlign: "left",
    },
    infoNoteText: {
      fontSize: "13px",
      color: "var(--text-color)",
      opacity: 0.7,
      lineHeight: "1.5",
      margin: 0,
    },
    inputGroup: {
      marginBottom: "4px",
    },
  };

  // ─── Success Page ───────────────────────────────────────────
  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Refund Submitted | {SITENAME}</title>
        </Helmet>
        {showLoader && <Loader />}
        <div style={styles.pageWrapper}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.headerIcon}>
                <i
                  className="fa fa-check"
                  style={{ color: "#fff", fontSize: "20px" }}
                ></i>
              </div>
              <h2 style={styles.headerTitle}>Refund Address Received</h2>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.successWrapper}>
                <div style={styles.successIcon}>
                  <i
                    className="fa fa-circle-check"
                    style={{ color: "#27ae60", fontSize: "36px" }}
                  ></i>
                </div>
                <h3 style={styles.successTitle}>Successfully Submitted</h3>
                <p style={styles.successSubtitle}>
                  Your refund address has been successfully submitted.
                  <br />
                  Your refund will be processed shortly.
                </p>

                <div style={styles.successCard}>
                  <div style={styles.successRow}>
                    <span style={styles.successLabel}>Refund Amount</span>
                    <span style={styles.successValue}>
                      {refundData.refundAmount} {refundData.currency}
                    </span>
                  </div>
                  <div style={styles.divider}></div>
                  <div style={styles.successRow}>
                    <span style={styles.successLabel}>Wallet Address</span>
                    <span
                      style={{
                        ...styles.successValue,
                        fontSize: "12px",
                        maxWidth: "220px",
                        wordBreak: "break-all",
                        textAlign: "right",
                      }}
                    >
                      {walletAddress}
                    </span>
                  </div>
                  <div style={styles.divider}></div>
                  <div style={styles.successRow}>
                    <span style={styles.successLabel}>Network</span>
                    <span style={styles.successValue}>
                      {selectedNetwork || refundData.networkName}
                    </span>
                  </div>
                </div>

                <div style={styles.infoNote}>
                  <i
                    className="fa fa-info-circle"
                    style={{
                      color: "var(--btn-color)",
                      fontSize: "16px",
                      marginTop: "2px",
                      flexShrink: 0,
                    }}
                  ></i>
                  <p style={styles.infoNoteText}>
                    Please allow some time for the refund to be processed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── Main Form ──────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Refund Request | {SITENAME}</title>
      </Helmet>
      {(showLoader || validatingAddress) && <Loader />}
      <div style={styles.pageWrapper}>
        <div style={styles.card}>
          {/* Header */}
          <div style={styles.cardHeader}>
            <div style={styles.headerIcon}>
              <i
                className="fa fa-rotate-left"
                style={{ color: "#fff", fontSize: "20px" }}
              ></i>
            </div>
            <h2 style={styles.headerTitle}>Refund Request</h2>
            {refundData.merchantName && (
              <p style={styles.merchantName}>{refundData.merchantName}</p>
            )}
          </div>

          {/* Body */}
          <div style={styles.cardBody}>
            {/* Refund Amount Badge */}
            <div style={styles.refundBadge}>
              <div style={{ textAlign: "center" }}>
                <p style={styles.refundLabel}>Refund Amount</p>
                <p style={styles.refundValue}>
                  {getCurrencyIcon() && (
                    <img
                      src={getCurrencyIcon()}
                      alt={refundData.currency}
                      style={styles.currencyIcon}
                    />
                  )}
                  {refundData.refundAmount}{" "}
                  <span style={styles.currencyTag}>{refundData.currency}</span>
                </p>
              </div>
            </div>

            {/* Refund Details */}
            <div style={styles.successCard}>
              {refundData.orderId !== "" &&
                refundData.orderId !== undefined && (
                  <>
                    <div style={styles.successRow}>
                      <span style={styles.successLabel}>Refund ID</span>
                      <span style={styles.successValue}>
                        #{refundData.orderId}
                      </span>
                    </div>
                    <div style={styles.divider}></div>
                  </>
                )}
              {refundData.transactionId !== "" &&
                refundData.transactionId !== 0 && (
                  <>
                    <div style={styles.successRow}>
                      <span style={styles.successLabel}>Transaction ID</span>
                      <span style={styles.successValue}>
                        {refundData.transactionId}
                      </span>
                    </div>
                    <div style={styles.divider}></div>
                  </>
                )}
              {refundData.invoiceAmount !== "" &&
                Number(refundData.invoiceAmount) > 0 && (
                  <>
                    <div style={styles.successRow}>
                      <span style={styles.successLabel}>Invoice Amount</span>
                      <span style={styles.successValue}>
                        {refundData.invoiceAmount} {refundData.currency}
                      </span>
                    </div>
                    <div style={styles.divider}></div>
                  </>
                )}
              {refundData.networkFeeAmount !== "" &&
                Number(refundData.networkFeeAmount) > 0 && (
                  <>
                    <div style={styles.successRow}>
                      <span style={styles.successLabel}>Network Fee</span>
                      <span style={styles.successValue}>
                        {refundData.networkFeeAmount} {refundData.currency}
                      </span>
                    </div>
                    <div style={styles.divider}></div>
                  </>
                )}
              <div style={styles.successRow}>
                <span style={styles.successLabel}>Network</span>
                <span style={styles.successValue}>
                  {refundData.networkName || "—"}
                </span>
              </div>
            </div>

            {/* Description */}
            <p style={styles.description}>
              Please enter the wallet address where you would like to receive
              your refund.
            </p>

            {/* Wallet Address Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Wallet Address</label>
              <input
                type="text"
                placeholder={`Enter your ${
                  refundData.currency || "crypto"
                } wallet address`}
                value={walletAddress}
                onChange={(e) => {
                  setWalletAddress(e.target.value);
                  setAddressError("");
                  setIsAddressValid(false);
                }}
                style={{
                  ...styles.input,
                  ...(addressError
                    ? isAddressValid
                      ? styles.inputSuccess
                      : styles.inputError
                    : {}),
                }}
              />
              {addressError && (
                <div
                  className={
                    isAddressValid
                      ? "refund-address-success-text"
                      : "refund-address-error-text"
                  }
                  style={isAddressValid ? styles.successText : styles.errorText}
                >
                  <i
                    className={
                      isAddressValid
                        ? "fa fa-check-circle"
                        : "fa fa-exclamation-circle"
                    }
                    style={{ marginTop: "2px", flexShrink: 0 }}
                  ></i>
                  <span>{addressError}</span>
                </div>
              )}
              <style>{`
                .refund-address-success-text,
                .refund-address-success-text * {
                  color: #27ae60 !important;
                }
                .refund-address-error-text,
                .refund-address-error-text * {
                  color: #e74c3c !important;
                }
              `}</style>
            </div>

            {/* Memo / Destination Tag (optional, for coins like XRP) */}
            {supportsMemo && (
              <div style={{ ...styles.inputGroup, marginTop: "16px" }}>
                <label style={styles.label}>
                  Memo / Destination Tag{" "}
                  <span style={{ fontWeight: "400", opacity: 0.5 }}>
                    (optional)
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Enter memo or destination tag if required"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  style={styles.input}
                />
              </div>
            )}

            {/* Network Selection */}
            {networks.length > 1 && (
              <div style={styles.networkSection}>
                <label style={styles.label}>
                  Network{" "}
                  <span style={{ fontWeight: "400", opacity: 0.5 }}>
                    ({refundData.currency})
                  </span>
                </label>
                <div style={styles.networkButtons}>
                  {networks.map((net) => (
                    <button
                      key={net}
                      type="button"
                      style={
                        selectedNetwork === net
                          ? styles.networkBtnActive
                          : styles.networkBtn
                      }
                      onClick={() => {
                        setSelectedNetwork(net);
                        setAddressError("");
                        setIsAddressValid(false);
                      }}
                    >
                      {net}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Show single network info */}
            {networks.length === 1 && (
              <div style={styles.networkSection}>
                <label style={styles.label}>
                  Network{" "}
                  <span
                    style={{
                      fontWeight: "400",
                      opacity: 0.6,
                      marginLeft: "6px",
                    }}
                  >
                    {networks[0]}
                  </span>
                </label>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="button"
              style={{
                ...styles.submitBtn,
                ...(!walletAddress.trim() ||
                validatingAddress ||
                (networks.length > 1 && !selectedNetwork)
                  ? styles.submitBtnDisabled
                  : {}),
              }}
              disabled={
                !walletAddress.trim() ||
                validatingAddress ||
                (networks.length > 1 && !selectedNetwork)
              }
              onClick={validateAddress}
            >
              <i className="fa fa-paper-plane"></i>
              {validatingAddress ? "Validating..." : "Submit Address"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundAddress;
